/* Editable Product Facts is the saved source for nutrition popups and automatic highlights. */
(() => {
  const normalize = value => String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
  let instance = 0;
  class FactsTabs extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.panels = [...this.querySelector('.bf-facts__stage')?.children || []].filter(panel => panel.hasAttribute('data-facts-panel'));
      const list = this.querySelector('.bf-facts__tablist');
      if (!list || !this.panels.length) return;
      list.replaceChildren();
      this.tabs = [];
      const prefix = `Facts-${++instance}`;
      this.panels.forEach((panel, i) => {
        panel.hidden = false;
        panel.removeAttribute('aria-labelledby');
        if (this.panels.length === 1) { panel.setAttribute('role', 'region'); return; }
        const tab = document.createElement('button');
        tab.type = 'button'; tab.className = 'bf-facts__tab'; tab.setAttribute('role', 'tab');
        tab.id = `${prefix}-tab-${i}`; panel.id = `${prefix}-panel-${i}`;
        tab.setAttribute('aria-controls', panel.id);
        tab.style.cssText = panel.style.cssText;
        const dot = document.createElement('i'); dot.className = 'bf-facts__dot'; dot.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span'); label.textContent = panel.dataset.factsTab || 'Product';
        tab.append(dot, label); list.append(tab); this.tabs.push(tab);
        panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', tab.id);
        tab.addEventListener('click', () => this.show(i), {signal});
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.tabs.length - 1;
          else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % this.tabs.length;
          else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (i - 1 + this.tabs.length) % this.tabs.length;
          if (next === undefined) return;
          event.preventDefault(); this.show(next, true);
        }, {signal});
      });
      list.hidden = this.panels.length < 2;
      this.addEventListener('shopify:block:select', event => {
        const index = this.panels.indexOf(event.target.closest('[data-facts-panel]'));
        if (index >= 0) this.show(index);
      }, {signal});
      this.show(Math.min(this.panels.length - 1, Math.max(0, Number(this.dataset.default || 1) - 1)));
      this.setAttribute('data-ready', '');
    }
    disconnectedCallback() { this.controller?.abort(); }
    show(index, focus = false) {
      this.panels.forEach((panel, i) => { panel.hidden = i !== index; });
      this.tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      if (focus) this.tabs[index]?.focus();
    }
  }
  if (!customElements.get('bf-facts-tabs')) customElements.define('bf-facts-tabs', FactsTabs);

  let config = {};
  try { config = JSON.parse(document.querySelector('[data-shared-facts-config]')?.textContent || '{}'); } catch (_) { /* No connection until configured. */ }
  const enabled = config.enabled === true;
  const profiles = new Map(), productProfiles = new Map();
  let source, pending, status = enabled ? 'idle' : 'disabled', openRequest = 0;
  const notify = () => document.dispatchEvent(new CustomEvent('bellyful:facts-loaded', {detail:{status}}));
  function readSource(section) {
    const next = new Map(), products = new Map(), duplicateKeys = new Set(), duplicateProducts = new Set();
    const panels = [...section.querySelector('bf-facts-tabs > .bf-facts__stage')?.children || []].filter(panel => panel.hasAttribute('data-facts-panel'));
    if (!panels.length) throw new Error('No product facts panels');
    panels.forEach((panel, index) => {
      const key = panel.dataset.factsKey?.trim(), productId = panel.dataset.factsProduct?.trim();
      const label = panel.querySelector('.bf-facts__label');
      const rows = [...label?.querySelectorAll('.bf-facts__row > tr:first-child') || []].map(row => ({
        name: row.querySelector('th[scope="row"]')?.textContent.trim(), amount: row.querySelector('td')?.textContent.trim()
      })).filter(row => row.name);
      const servingLine = [...label?.querySelectorAll('.bf-facts__serving') || []].map(line => line.textContent).find(line => /servings\s+per\s+container/i.test(line));
      const servings = servingLine?.match(/servings\s+per\s+container\s*:?\s*(\d+(?:\.\d+)?)/i)?.[1] || '';
      const highlights = [1,2,3].map(i => {
        const nutrient = panel.dataset[`highlight${i}Row`], display = panel.dataset[`highlight${i}Label`];
        const amount = normalize(nutrient) === '@servings' ? servings : rows.find(row => normalize(row.name) === normalize(nutrient))?.amount;
        return amount && display ? {value:String(amount).replace(/\s+/g, ''), label:display} : null;
      }).filter(Boolean);
      const profile = {key, productId, highlights, index};
      if (key) { if (next.has(key)) duplicateKeys.add(key); next.set(key, profile); }
      if (productId) { if (products.has(productId)) duplicateProducts.add(productId); products.set(productId, profile); }
    });
    duplicateKeys.forEach(key => next.delete(key)); duplicateProducts.forEach(id => products.delete(id));
    profiles.clear(); next.forEach((value,key) => profiles.set(key,value));
    productProfiles.clear(); products.forEach((value,key) => productProfiles.set(key,value));
    source = section; status = 'ready'; notify();
  }
  function localSource() {
    const sections = document.querySelectorAll('.bf-facts[data-shared-facts-source="true"]');
    if (sections.length !== 1) return false;
    readSource(sections[0]); return true;
  }
  function sourceURL() {
    const url = new URL(config.pageUrl || `${config.root || '/'}pages/nutrition-facts`, location.origin);
    if (url.origin !== location.origin) throw new Error('Facts page must use the store origin');
    const current = new URL(location.href);
    // Keep draft theme previews in the same theme. Never add preview parameters to customer links.
    const previewId = current.searchParams.get('preview_theme_id') || (window.Shopify?.theme?.role !== 'main' && window.Shopify?.theme?.id);
    if (previewId) url.searchParams.set('preview_theme_id', String(previewId));
    return url;
  }
  async function load() {
    if (!enabled) return false;
    if (status === 'ready') return true;
    if (pending) return pending;
    try { if (localSource()) return true; } catch (_) { /* Try the configured page. */ }
    pending = (async () => {
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 8000);
      try {
        status = 'loading';
        const response = await fetch(sourceURL(), {credentials:'same-origin', cache:'no-store', signal:controller.signal});
        if (!response.ok) throw new Error('Facts page could not load');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const sections = html.querySelectorAll('.bf-facts[data-shared-facts-source="true"]');
        if (sections.length !== 1) throw new Error('Choose one shared Product Facts section');
        readSource(sections[0]); return true;
      } catch (_) { status = 'failed'; notify(); return false; }
      finally { clearTimeout(timeout); pending = null; }
    })();
    return pending;
  }
  function get(key, productId) { return profiles.get(String(key || '').trim()) || productProfiles.get(String(productId || '')); }
  function popup(host, profile) {
    const clone = source.cloneNode(true);
    clone.dataset.sharedFactsSource = 'false';
    clone.classList.add('bf-facts--popup');
    clone.querySelector('.bf-facts__intro')?.remove();
    clone.querySelectorAll('script,iframe,form').forEach(node => node.remove());
    const ids = new Map();
    clone.querySelectorAll('[id]').forEach(node => { const old = node.id; node.id = `Popup-${++instance}-${old}`; ids.set(old,node.id); });
    clone.querySelectorAll('*').forEach(node => {
      [...node.attributes].filter(attr => attr.name.startsWith('on') || attr.name.startsWith('data-shopify')).forEach(attr => node.removeAttribute(attr.name));
      for (const name of ['aria-controls','aria-labelledby','aria-describedby','for']) {
        if (node.hasAttribute(name)) node.setAttribute(name,node.getAttribute(name).split(/\s+/).map(id => ids.get(id) || id).join(' '));
      }
    });
    const tabs = clone.querySelector('bf-facts-tabs');
    tabs.removeAttribute('data-ready'); tabs.dataset.default = String(profile.index + 1);
    clone.querySelectorAll('[data-facts-panel]').forEach(panel => { panel.hidden = false; });
    const legacyTabs = host.querySelector('[data-nutrition-tabs]'); legacyTabs.replaceChildren(); legacyTabs.hidden = true;
    const panel = host.querySelector('[data-nutrition-panel]'); panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby'); panel.replaceChildren(clone);
    host.classList.add('bf-nutrition-dialog--shared');
  }
  async function open(key, trigger) {
    if (!enabled) return false;
    const request = ++openRequest;
    if (!await load()) return false;
    if (request !== openRequest || !trigger.isConnected) return true;
    const host = document.getElementById('BellyfulNutrition'); if (!host) return true;
    const composer = trigger.closest('product-composer');
    const variant = composer?.config?.variants.find(v => v.options[composer.flavorIndex] === composer.activeFlavor);
    const profile = get(key, trigger.dataset.nutritionProduct || variant?.productId || composer?.dataset.productId);
    host._returnFocus = trigger;
    if (profile) popup(host,profile);
    else {
      host.classList.remove('bf-nutrition-dialog--shared');
      host.querySelector('[data-nutrition-tabs]').replaceChildren(); host.querySelector('[data-nutrition-tabs]').hidden = true;
      const panel = host.querySelector('[data-nutrition-panel]'); panel.removeAttribute('role'); panel.removeAttribute('aria-labelledby');
      const message = document.createElement('p'); message.textContent = 'Product facts are not available for this selection.';
      const link = document.createElement('a'); link.href = config.pageUrl || `${config.root || '/'}pages/nutrition-facts`; link.textContent = 'View nutrition facts';
      panel.replaceChildren(message,link);
    }
    if (!host.open) host.showModal();
    host.querySelector('[data-dialog-close]')?.focus(); return true;
  }
  window.BellyfulFacts = {load,get,open,get status(){return status;},get enabled(){return enabled;}};
  document.addEventListener('shopify:section:load', () => { if (enabled) { try { localSource(); } catch (_) { status='failed'; notify(); } } });
  if (enabled && (document.querySelector('.bf-facts[data-shared-facts-source="true"]') || document.querySelector('product-composer'))) load();
})();
